import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  useToast,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

const GET_SESSION_TYPE = gql`
  query GetSessionTypeById($id: ID!) {
    sessionTypeById(id: $id) {
      id
      name
      description
      format
      duration
      price
      color
      availableOnCalendar
      meetingLink
    }
  }
`;

const UPDATE_SESSION_TYPE = gql`
  mutation UpdateSessionType($id: ID!, $input: SessionTypeInput!) {
    updateSessionType(id: $id, input: $input) {
      id
      name
      description
      format
      duration
      price
      color
      availableOnCalendar
      meetingLink
    }
  }
`;

interface SessionTypeFormData {
  name: string;
  description?: string;
  format: "INDIVIDUAL" | "GROUP";
  duration: number;
  price: number;
  color?: string;
  availableOnCalendar: boolean;
  meetingLink?: string;
}

export const EditSessionType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState<SessionTypeFormData | null>(null);

  // Brand styling variables
  const bgMain = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");
  const primaryColor = getColor("primary");
  const primaryHover = getColor("primaryHover");

  const { data: queryData, loading: queryLoading } = useQuery(GET_SESSION_TYPE, {
    variables: { id },
    onCompleted: (data) => {
      console.log("Received session type data:", data.sessionTypeById);
      if (data.sessionTypeById) {
        setFormData(data.sessionTypeById);
      }
    },
  });

  const [updateSessionType, { loading: updateLoading }] = useMutation(UPDATE_SESSION_TYPE);

  if (queryLoading || !formData) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.md" py={8} flex="1">
          <Text color={textPrimary} fontFamily={brandConfig.fonts.body}>
            Loading...
          </Text>
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Submitting updated session type:", {
      id,
      input: {
        name: formData.name,
        description: formData.description,
        format: formData.format,
        duration: Number(formData.duration),
        price: Number(formData.price),
        color: formData.color,
        availableOnCalendar: formData.availableOnCalendar,
        meetingLink: formData.meetingLink
      }
    });

    try {
      const { data } = await updateSessionType({
        variables: {
          id,
          input: {
            name: formData.name,
            description: formData.description,
            format: formData.format,
            duration: Number(formData.duration),
            price: Number(formData.price),
            color: formData.color,
            availableOnCalendar: formData.availableOnCalendar,
            meetingLink: formData.meetingLink
          }
        },
        refetchQueries: ["GetSessionTypes"]
      });

      console.log("Updated session:", data.updateSessionType);

      toast({
        title: "Session type updated",
        status: "success",
        duration: 3000,
      });
      navigate("/sessions/types");
    } catch (error) {
      console.error("Error updating session type:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={8} flex="1">
        <VStack spacing={8} align="stretch">
          <Heading size="lg" color={textPrimary} fontFamily={brandConfig.fonts.heading}>
            Edit Session Type
          </Heading>

          <Box
            p={6}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Name
                  </FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    bg="rgba(255, 255, 255, 0.05)"
                    borderColor={cardBorder}
                    color={textPrimary}
                    _placeholder={{ color: textMuted }}
                    _hover={{ borderColor: textSecondary }}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    fontFamily={brandConfig.fonts.body}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Description
                  </FormLabel>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    bg="rgba(255, 255, 255, 0.05)"
                    borderColor={cardBorder}
                    color={textPrimary}
                    _placeholder={{ color: textMuted }}
                    _hover={{ borderColor: textSecondary }}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    fontFamily={brandConfig.fonts.body}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Format
                  </FormLabel>
                  <Select
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value as "INDIVIDUAL" | "GROUP" })}
                    bg="rgba(255, 255, 255, 0.05)"
                    borderColor={cardBorder}
                    color={textPrimary}
                    _hover={{ borderColor: textSecondary }}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    fontFamily={brandConfig.fonts.body}
                  >
                    <option value="INDIVIDUAL" style={{ background: '#1a202c', color: '#e2e8f0' }}>Individual</option>
                    <option value="GROUP" style={{ background: '#1a202c', color: '#e2e8f0' }}>Group</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Duration (minutes)
                  </FormLabel>
                  <NumberInput
                    value={formData.duration}
                    onChange={(_, value) => setFormData({ ...formData, duration: value })}
                    min={15}
                    step={15}
                  >
                    <NumberInputField
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                      <NumberDecrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Price ($)
                  </FormLabel>
                  <NumberInput
                    value={formData.price}
                    onChange={(_, value) => setFormData({ ...formData, price: value })}
                    min={0}
                    precision={2}
                  >
                    <NumberInputField
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                      <NumberDecrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Color
                  </FormLabel>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    bg="rgba(255, 255, 255, 0.05)"
                    borderColor={cardBorder}
                    _hover={{ borderColor: textSecondary }}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color={textSecondary} fontFamily={brandConfig.fonts.body}>
                    Meeting Link (optional)
                  </FormLabel>
                  <Input
                    placeholder="https://zoom.us/j/example"
                    value={formData.meetingLink || ""}
                    onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                    bg="rgba(255, 255, 255, 0.05)"
                    borderColor={cardBorder}
                    color={textPrimary}
                    _placeholder={{ color: textMuted }}
                    _hover={{ borderColor: textSecondary }}
                    _focus={{
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 1px ${primaryColor}`
                    }}
                    fontFamily={brandConfig.fonts.body}
                  />
                </FormControl>

                <FormControl>
                  <Checkbox
                    isChecked={formData.availableOnCalendar}
                    onChange={(e) => setFormData({ ...formData, availableOnCalendar: e.target.checked })}
                    colorScheme="blue"
                    borderColor={cardBorder}
                    color={textSecondary}
                    fontFamily={brandConfig.fonts.body}
                    _checked={{
                      bg: primaryColor,
                      borderColor: primaryColor,
                      color: "white"
                    }}
                  >
                    Available on calendar
                  </Checkbox>
                </FormControl>

                <Button
                  type="submit"
                  bg={primaryColor}
                  color="white"
                  w="full"
                  isLoading={updateLoading}
                  _hover={{
                    bg: primaryHover,
                    transform: "translateY(-2px)"
                  }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.2s"
                  fontWeight="semibold"
                  fontFamily={brandConfig.fonts.body}
                  size="lg"
                  mt={4}
                >
                  Update Session Type
                </Button>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
      <Box position="sticky" bottom={0} zIndex={10}>
        <FooterWithFourColumns />
      </Box>
    </Box>
  );
}; 